import { type SelectOption } from '@/components/forms';
import { type Crumb, type NextStep, type PageIntroHighlight } from '@/components/molecules';
import { FLEET_QUOTE_ANCHOR } from '@/config/navigation';
import { type ClientSector, clients, clientSectorGroups } from '@/content/clients';

/**
 * Page-local copy and ids for /clients.
 *
 * Every figure stated here is counted from `content/clients.ts` rather than
 * typed — the roster grew several times over in one sitting, and a
 * hand-written "over 50 organisations" is the sentence that goes stale first
 * and is least forgivable when it does: this page's whole purpose is to be
 * checkable.
 *
 * That rule now covers the page's selling copy as well as its headings. The
 * roster is the argument, so every persuasive line below is either arithmetic
 * on it, a fact already published elsewhere on this site (the itemised
 * quotation, the reference number, the same-day reply), or a statement about
 * what the form does. No testimonial, no award, no invented percentage: the
 * page asks an institutional buyer to put us in front of their own finance
 * office, and the first unverifiable number is the one that gets us thrown
 * out of that meeting.
 */

/** `Breadcrumbs` renders Home itself; the schema has to be told about it. */
export const crumbs: readonly Crumb[] = [{ label: 'Our Clients' }];

export const ROSTER_TITLE_ID = 'clients-roster-title';
export const FLEET_TITLE_ID = 'clients-fleet-title';

/**
 * The anchor the page's conversion point answers to.
 *
 * It is a published id, not an implementation detail: the opening band links
 * down to it, the sector call to action inside the roster links down to it,
 * and the home page's client wall links across to it. Because that last one
 * is another page, the string itself lives in `config/navigation` beside the
 * link built from it, and this is the alias the band and its two in-page
 * anchors read.
 */
export const FLEET_FORM_ID = FLEET_QUOTE_ANCHOR;

export const WHATSAPP_MESSAGE =
  'Hello Kabex Motors, I am buying for a company and would like to discuss a vehicle or a fleet.';

export const clientCount = clients.length;
export const sectorCount = clientSectorGroups.length;

/**
 * Three claims, all of them arithmetic on the roster below. No award, no
 * rating and no "trusted by thousands": the page is a list, and its
 * credibility comes from being countable.
 */
export const introHighlights: readonly PageIntroHighlight[] = [
  { icon: 'building', label: `${clientCount} organisations` },
  { icon: 'route', label: `${sectorCount} sectors` },
  { icon: 'car', label: 'Single cars and fleets' },
];

/**
 * The opening band's button, and the one place on the page that tells a
 * skimmer there is something to do here.
 *
 * A reader who arrives already convinced — sent this link by a colleague, or
 * back for the second time — should not have to scroll the whole roster to
 * find the form. It is an in-page anchor rather than a route because the thing
 * it points at is on this page; `usePageIntroActions` reads the leading `#`
 * and renders it as an anchor rather than a router link.
 */
export const introAction = {
  label: 'Request an itemised quotation',
  to: `#${FLEET_FORM_ID}`,
};

export const rosterLede = `Government ministries, banks, universities, hospitals, energy projects, tour operators and manufacturers. Filter by sector to find the organisations closest to your own.`;

/** The chip that clears the sector filter. Labelled with the total it returns to. */
export const allSectorsLabel = `All sectors (${clientCount})`;
export const sectorGroupLabel = 'Filter by sector';

/**
 * The call to action that sits at the foot of the roster, in the two states
 * the roster has.
 *
 * It is a function of the filter on purpose. A logo wall is the weakest kind
 * of proof while it is only a wall — the visitor has to do the work of
 * deciding whether anyone on it resembles them — and the filter is the moment
 * they finish doing exactly that. Someone who has just tapped
 * "Banking & Insurance" has told us what they are; answering with the count
 * they are looking at, and an offer in the same sentence, is the strongest
 * position this page ever holds. So the ask is made there, beside the proof,
 * and not only in a band at the bottom.
 *
 * Both states are countable claims. Neither says anything about the
 * organisations above beyond the fact that they bought vehicles here.
 */
export function sectorCtaCopy(sector: ClientSector | null, shown: number) {
  if (!sector) {
    return {
      eyebrow: 'Buying for an organisation',
      title: 'Your organisation would be the next name on this list',
      text: `${clientCount} ministries, banks, universities, hospitals and companies across ${sectorCount} sectors buy their vehicles through us. Tell us what yours needs and we will price it — the vehicle, the shipping and the URA taxes set out separately.`,
    };
  }
  const plural = shown === 1 ? 'organisation' : 'organisations';
  return {
    eyebrow: sector,
    title: `${shown} ${sector} ${plural} already import through us`,
    text: `Yours would be sourced, shipped and cleared the same way. Tell us the vehicles and we will send a quotation with the vehicle, the shipping and the URA taxes set out separately.`,
  };
}

/** The button beside that copy, and the submit button on the form it opens. */
export const fleetCtaLabel = 'Request an itemised quotation';
export const fleetSubmitLabel = 'Request My Quotation';

/**
 * WhatsApp, carrying whichever sector the reader filtered to.
 *
 * The page's route handle supplies the generic message for the floating
 * button and the closing band (§60 K/L). This is the one place that says
 * something narrower, because it is the one control a reader reaches with a
 * sector already in mind — and a message that arrives naming the sector saves
 * the first question of the conversation.
 */
export function sectorWhatsAppMessage(sector: ClientSector | null): string {
  return sector
    ? `Hello Kabex Motors, I am buying for an organisation in ${sector} and would like a quotation.`
    : WHATSAPP_MESSAGE;
}

export const fleetEyebrow = 'Request a quotation';
export const fleetTitle = 'One executive car, or a fleet';
export const fleetLede = `Every organisation above started with a request like this one. Tell us what you need and who to send the quotation to — there is nothing to pay and nothing is reserved.`;

/**
 * What the sender gets, written as four things that are true of this form
 * rather than four adjectives about the company.
 *
 * Each line is checkable by the reader within a minute of sending: the price
 * is itemised, no payment is asked for, a reference number comes back, and
 * the roster above is the size it says it is.
 */
export const fleetAssurances: readonly string[] = [
  'No deposit, no payment and no commitment to request one.',
  'The vehicle, the shipping and the URA taxes set out separately.',
  'A reference number comes back the moment you send it.',
  `The same process the ${clientCount} organisations above went through.`,
];

/**
 * What happens after the form is sent.
 *
 * The single biggest thing standing between an institutional buyer and a
 * submit button is not price, it is not knowing what the button starts. Three
 * steps, none of them promising a date the business cannot keep: a reply, a
 * written quotation, and the fact that neither obliges the organisation to
 * anything.
 */
export const fleetSteps: readonly NextStep[] = [
  {
    title: 'A person replies, usually the same day',
    text: 'On WhatsApp or by phone, to confirm the specification, how many vehicles you need and where they are going.',
  },
  {
    title: 'Your quotation is written up',
    text: 'The vehicle, the shipping and the URA taxes set out separately, so your finance office can check the figure line by line.',
  },
  {
    title: 'Nothing is paid or reserved until you say so',
    text: 'Asking for a quotation commits your organisation to nothing. Keep the reference number and we can pick the request up exactly where it stopped.',
  },
];

/**
 * The sector picker on the form, offered in the roster's own vocabulary so a
 * lead can be counted against the group it belongs to. Derived from the
 * groups, never retyped — a sector added to `content/clients.ts` appears here
 * without this file being touched.
 */
export const fleetSectorOptions: readonly SelectOption[] = clientSectorGroups.map((group) => ({
  value: group.sector,
  label: group.sector,
}));
